import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  storageApi,
  type CreateStorageNodePayload,
  type DeclareGridPayload,
  type FreeSlotsParams,
  type LayoutLevelPayload,
  type ListStorageNodesParams,
  type UpdateStorageNodePayload,
} from '@/api/storageApi'
import { useTenant } from '@/providers/TenantProvider'
import { toNodeKind, type WizardLevel } from '@/lib/storageWizard'
import type {
  SlotLabelScheme,
  StorageCondition,
  StorageConditionInput,
  StorageConditionPatch,
  StorageTreeNode,
} from '@/types/storage'

/**
 * Storage-hierarchy queries and mutations.
 *
 * `selectedTenantId` is in every query key so switching tenant re-fetches
 * rather than painting another tenant's shelves, per the repo's tenancy rule.
 */

const KEY = 'storage-nodes'

export function useStorageNodes(params: ListStorageNodesParams = {}, enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'list', tenantId, params],
    enabled: !!tenantId && enabled,
    queryFn: () => storageApi.listNodes(tenantId as string, params),
  })
}

/**
 * One node by id — for resolving a position to the unit it lives in.
 *
 * The tree hooks all take a ROOT; this is the only way to look up a node when
 * all you hold is its id, which is what an audit row gives you.
 */
export function useStorageNode(nodeId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'node', tenantId, nodeId],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.getNode(tenantId as string, nodeId as string),
  })
}

/** The storage units — the roots of every tree in this tenant. */
export function useStorageRoots(enabled = true) {
  return useStorageNodes({ rootsOnly: true }, enabled)
}

export function useStorageSubtree(nodeId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'subtree', tenantId, nodeId],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.getSubtree(tenantId as string, nodeId as string),
  })
}

/**
 * The free slots of a unit, for every slot picker (z8nrz7fqfy): declared slots
 * included, one page of them, with the containers above. Use this rather than
 * `useStorageSubtree` wherever stock is put somewhere — the subtree lists
 * stored rows only, so a unit whose slots are declared reads as full.
 */
export function useFreeSlots(nodeId: string | null | undefined, params: FreeSlotsParams = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  const { limit, condition, include } = params
  return useQuery({
    queryKey: [KEY, 'free-slots', tenantId, nodeId, limit ?? null, condition ?? null, include ?? null],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.getFreeSlots(tenantId as string, nodeId as string, { limit, condition, include }),
  })
}

/**
 * The containers of a unit, each with its capacity already worked out.
 *
 * What the hierarchy pane reads. It replaces the full-subtree fetch, which sent
 * every slot to draw a list that never shows one — measured on a 49,641-node
 * freezer, 1,569 ms and 29.4 MB became 170 ms and 0.84 MB. The slot map asks
 * for its own container's cells separately.
 */
export function useStorageTree(nodeId: string | null | undefined, depth?: number) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'tree', tenantId, nodeId, depth ?? 'all'],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.getTree(tenantId as string, nodeId as string, depth),
  })
}

/**
 * The unit's structure, loaded one level at a time.
 *
 * A freezer of a hundred racks holding a hundred boxes each is 4.9 MB of
 * containers, and the pane shows a hundred rows of it. This fetches the unit's
 * own children on arrival and a branch's children when that branch is opened,
 * merging them into one list so everything downstream — the tree, the fill
 * bars, the breadcrumbs — carries on reading a flat array of nodes.
 *
 * Each level is its own query, so an opened branch stays cached and reopening
 * it is instant.
 */
export function useLazyStorageTree(rootId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  // Which branches have been opened. The root is always one of them.
  const [loaded, setLoaded] = useState<string[]>([])

  useEffect(() => {
    setLoaded(rootId ? [rootId] : [])
  }, [rootId])

  const results = useQueries({
    queries: loaded.map((id) => ({
      queryKey: [KEY, 'tree', tenantId, id, 1],
      enabled: !!tenantId,
      queryFn: () => storageApi.getTree(tenantId as string, id, 1),
      // A level does not change while you are looking at it, and reopening a
      // branch should not refetch what is already on screen.
      staleTime: 5 * 60_000,
    })),
  })

  const nodes = useMemo(() => {
    // Deduplicated by id: every level includes its own root, so a branch's
    // first node is already present from the level above it.
    const byId = new Map<string, StorageTreeNode>()
    for (const result of results) {
      for (const node of result.data?.containers ?? []) byId.set(node.id, node)
    }
    return [...byId.values()]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.dataUpdatedAt).join(',')])

  const ensureLoaded = useCallback((id: string) => {
    setLoaded((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  return {
    nodes,
    ensureLoaded,
    /**
     * How many containers the unit holds — not how many have been loaded.
     *
     * Every level reports it, so the unit's own read answers it before anything
     * else arrives. It is what the panel sizes its decisions on.
     */
    containerCount: results[0]?.data?.containerCount ?? null,
    /** True until the unit's own level has arrived — nothing can be drawn yet. */
    isLoading: results.length === 0 || results[0]?.isLoading === true,
    isError: results.some((r) => r.isError),
    error: results.find((r) => r.isError)?.error ?? null,
    refetch: () => results.forEach((r) => void r.refetch()),
  }
}

/**
 * Find a container anywhere under the unit.
 *
 * Server-side because the pane only holds what has been opened. Disabled for a
 * blank query so typing and clearing does not fire a request per keystroke of
 * deletion.
 */
export function useContainerSearch(rootId: string | null | undefined, q: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const query = q.trim()
  return useQuery({
    queryKey: [KEY, 'search', tenantId, rootId, query],
    enabled: !!tenantId && !!rootId && query.length > 0,
    queryFn: () => storageApi.searchContainers(tenantId as string, rootId as string, query),
  })
}

/**
 * The cells of ONE container, for a slot map.
 *
 * Read from its own endpoint rather than filtered out of the subtree, because
 * a position no longer necessarily has a row: a box's grid declaration
 * describes its hundred slots, and the server derives them on read. Filtering
 * the subtree would find nothing and draw an empty shelf.
 *
 * It is also the right shape regardless. The map draws one box, so it asks for
 * one box.
 */
export function useGridCells(nodeId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'cells', tenantId, nodeId],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.getGridCells(tenantId as string, nodeId as string),
  })
}

/**
 * Derived capacity for a subtree. Computed on the server on every read — there
 * is no stored count to go stale, so this is safe to refetch freely.
 */
export function useStorageCapacity(nodeId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'capacity', tenantId, nodeId],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.getCapacity(tenantId as string, nodeId as string),
  })
}

/**
 * Re-ask for what a put-away dialog decides from: which units have room, and
 * their free slots. Slot availability is volatile, so each open asks again.
 *
 * Only those two. Invalidating every storage query refetched the conditions
 * reference and whatever else the page held under this key, on every open,
 * in front of the dialog's own reads (z8nrz7fzk7).
 */
export function useRefreshStorageAvailability() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [KEY, 'capacity-summary'] })
    void queryClient.invalidateQueries({ queryKey: [KEY, 'free-slots'] })
  }, [queryClient])
}

/** The Figure-9 capacity table: one row per storage unit, all derived. */
/**
 * `enabled` so a dialog that is mounted but closed does not scan the tenant's
 * storage on every page open (z8nrz7e8kw).
 */
export function useCapacitySummary(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'capacity-summary', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => storageApi.capacitySummary(tenantId as string),
  })
}

/** "Where is lot X" — enabled once the query is two characters of signal. */
export function useLocateLots(q: string) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'locate', tenantId, q],
    enabled: !!tenantId && q.trim().length >= 2,
    queryFn: () => storageApi.locate(tenantId as string, q.trim()),
  })
}

/** Lots held anywhere beneath a node, each with its position path. */
export function useNodeContents(nodeId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'contents', tenantId, nodeId],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.contents(tenantId as string, nodeId as string),
  })
}

/** Stock already in the hierarchy, for the "jump to a lot" picker. */
export function usePlacedLots() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'placed', tenantId],
    enabled: !!tenantId,
    queryFn: () => storageApi.placed(tenantId as string),
  })
}

/**
 * Stock waiting to be put away. `requires` narrows it to what a given slot can
 * legally take, so the picker never offers a lot the server would refuse.
 */
export function useUnplacedLots(requires?: StorageCondition | null, enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'unplaced', tenantId, requires ?? null],
    enabled: !!tenantId && enabled,
    queryFn: () => storageApi.unplaced(tenantId as string, requires ?? null),
  })
}

/**
 * The tenant's storage conditions and the families they group into. Cached
 * hard: they change only through the mutations below, which invalidate it.
 */
export function useStorageConditions() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'conditions', tenantId],
    enabled: !!tenantId,
    staleTime: Infinity,
    queryFn: () => storageApi.conditions(tenantId as string),
  })
}

/**
 * Add / edit / delete one of the tenant's storage conditions. Each refreshes the
 * reference every picker reads, and the storage profiles' Storage Condition
 * field, whose options the server keeps in step with the conditions.
 */
export function useStorageConditionMutations() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: [KEY, 'conditions'] })
    queryClient.invalidateQueries({ queryKey: ['inventory', 'fieldDefs'] })
    // Adding a condition also creates its storage profile.
    queryClient.invalidateQueries({ queryKey: ['inventory', 'profiles'] })
    queryClient.invalidateQueries({ queryKey: ['storage-temp-mappings'] })
    // A temperature's Type moves its units between the list's family chips.
    queryClient.invalidateQueries({ queryKey: [KEY] })
  }
  const need = () => {
    if (!tenantId) throw new Error('No tenant selected')
    return tenantId
  }
  return {
    create: useMutation({
      mutationFn: (input: StorageConditionInput) => storageApi.createCondition(need(), input),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: StorageConditionPatch }) =>
        storageApi.updateCondition(need(), id, patch),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (id: string) => storageApi.deleteCondition(need(), id),
      onSuccess,
    }),
  }
}

export function useFreeSpace() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'free-space', tenantId],
    enabled: !!tenantId,
    queryFn: () => storageApi.freeSpace(tenantId as string),
  })
}

/** Location codes the StorageZone migration could not resolve, for review. */
export function useUnresolvedLocations() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'unresolved', tenantId],
    enabled: !!tenantId,
    queryFn: () => storageApi.listUnresolved(tenantId as string),
  })
}

/**
 * Retry every parked code against the shelves as they are now.
 *
 * Invalidates the whole storage cache, not just the parked list: a successful
 * pass moves stock, so occupancy, capacity and every unit's contents change too.
 */
export function useReresolveLocations() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: () => storageApi.reresolveUnresolved(tenantId as string),
    onSuccess: invalidate,
  })
}

/**
 * What deleting this node and its layout would remove.
 *
 * `enabled` on the id so the confirmation only asks when it is open — this is a
 * subtree scan, not something to run for every row of a table.
 */
export function useDeletableSummary(nodeId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'deletable', tenantId, nodeId],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.describeDeletable(tenantId as string, nodeId as string),
  })
}

/** Delete a node and its whole layout. The server refuses if stock is inside. */
export function useDeleteStorageSubtree() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: (id: string) => storageApi.deleteSubtree(tenantId as string, id),
    onSuccess: invalidate,
  })
}

/**
 * One node's audit trail.
 *
 * `enabled` on the id AND on `open`, so opening the Storage page does not fetch
 * a trail for every node the user merely clicked past — this is history, not
 * something any screen needs eagerly.
 */
export function useNodeAudit(
  nodeId: string | null | undefined,
  open = true,
  subtree = false,
) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    // `subtree` is in the key: the same node answers two different questions
    // ("here" vs "anywhere below here") and they must not share a cache entry.
    queryKey: [KEY, 'audit', 'node', tenantId, nodeId, subtree],
    enabled: !!tenantId && !!nodeId && open,
    queryFn: () =>
      storageApi.nodeAudit(tenantId as string, nodeId as string, subtree ? { subtree: true } : {}),
  })
}

/** One lot's trail across every position it has occupied. */
export function useLotAudit(lotId: string | null | undefined, open = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'audit', 'lot', tenantId, lotId],
    enabled: !!tenantId && !!lotId && open,
    queryFn: () => storageApi.lotAudit(tenantId as string, lotId as string),
  })
}

/** One zone's trail. Readable after the zone itself is deleted. */
export function useZoneAudit(zoneId: string | null | undefined, open = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'audit', 'zone', tenantId, zoneId],
    enabled: !!tenantId && !!zoneId && open,
    queryFn: () => storageApi.zoneAudit(tenantId as string, zoneId as string),
  })
}

/**
 * What compacting this unit would remove.
 *
 * `enabled` on the id for the same reason the deletable summary is: this scans
 * a subtree, and it is asked only when someone opens the question.
 */
export function useCompactionSummary(nodeId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'compactable', tenantId, nodeId],
    enabled: !!tenantId && !!nodeId,
    queryFn: () => storageApi.describeCompaction(tenantId as string, nodeId as string),
  })
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useStorageInvalidator() {
  const qc = useQueryClient()
  // Every mutation can change a path, an occupancy or a capacity somewhere in
  // the tree, so the whole storage namespace is invalidated rather than
  // guessing which slice moved.
  //
  // Deliberately still broad. The obvious narrowing — skip the subtree refetch
  // after a put-away, since stock does not change the shape of the tree — is
  // wrong twice over: the subtree payload carries each slot's `occupied` flag
  // and feeds the fill bars, and a put-away into a declared cell CREATES a row,
  // which changes the shape after all. The cost that made this worth narrowing
  // was the 29 MB subtree, and that is now addressed at the source: a declared
  // unit reads 1 MB, and an old one gets there by being compacted.
  return () => qc.invalidateQueries({ queryKey: [KEY] })
}

/**
 * Fold a unit's redundant cell rows back into the declarations that describe
 * them. Invalidates broadly because it removes rows across the whole subtree.
 */
export function useCompactStorageUnit() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      storageApi.compact(tenantId as string, id, reason),
    onSuccess: invalidate,
  })
}

export function useCreateStorageNode() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: (payload: CreateStorageNodePayload) =>
      storageApi.createNode(tenantId as string, payload),
    onSuccess: invalidate,
  })
}

export function useDeclareGrid() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: ({ id, ...payload }: DeclareGridPayload & { id: string }) =>
      storageApi.declareGrid(tenantId as string, id, payload),
    onSuccess: invalidate,
  })
}

/** Switch a declared grid's label scheme. Renames every cell; audited. */
export function useRelabelGrid() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; scheme: SlotLabelScheme; prefix?: string }) =>
      storageApi.relabel(tenantId as string, id, payload),
    onSuccess: invalidate,
  })
}

/**
 * The wizard's vocabulary → the wire's.
 *
 * A section is named (people call a compartment "Vapor", they do not number
 * it); everything else is counted by a grid. `toNodeKind` is the only crossing
 * point between the two vocabularies, exactly as in `storageWizard`.
 */
function toLayoutLevel(level: WizardLevel): LayoutLevelPayload {
  if (level.kind === 'SECTION') {
    return { kind: toNodeKind(level.kind), codes: level.names }
  }
  return {
    kind: toNodeKind(level.kind),
    rows: level.rows,
    columns: level.columns,
    scheme: level.scheme,
    ...(level.scheme === 'SEQUENTIAL' && level.prefix ? { prefix: level.prefix } : {}),
  }
}

/**
 * Build a whole layout from the wizard's described levels — in one request.
 *
 * This used to walk the plan level by level: declare a grid on the unit, re-read
 * the subtree to learn the ids it minted, declare a grid on each rack, re-read,
 * then on each box. The request count tracked the number of CONTAINERS, so a
 * plan with 200 boxes issued 200 declarations, tripped the API's 120-per-minute
 * rate limiter partway through, and left a half-built tree that nothing could
 * roll back — the user saw a layout that was silently missing its tail.
 *
 * The server now takes the whole plan and builds it in one transaction, so the
 * layout either exists or it does not, and the cost is a function of levels
 * rather than nodes. Measured: 32,820 nodes in a single 13s request.
 */
export function useBuildStorageLayout() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: async ({
      rootId,
      levels,
      copy,
    }: {
      rootId: string
      levels: WizardLevel[]
      /**
       * What a COPY brings across besides the shape. Sent on this one request
       * for the reason above: blocked slots as their own `setBlocked` calls
       * would reintroduce exactly the per-container request count that broke
       * the old wizard.
       */
      copy?: {
        providesCondition?: StorageCondition
        storageProfileId?: string
        blockedCells?: string[]
      }
    }) => {
      const result = await storageApi.buildLayout(
        tenantId as string,
        rootId,
        levels.map(toLayoutLevel),
        copy,
      )
      return { created: result.created }
    },
    onSuccess: invalidate,
  })
}

/**
 * An existing unit described as a layout, for copying onto another.
 *
 * `enabled` on the id so nothing is read until somebody actually picks a
 * source — this walks the source's whole container tree, which is not work to
 * do for a dialog that has only been opened.
 */
export function useLayoutTemplate(sourceId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'layout-template', tenantId, sourceId],
    enabled: !!tenantId && !!sourceId,
    queryFn: () => storageApi.layoutTemplate(tenantId as string, sourceId as string),
  })
}

export function useUpdateStorageNode() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateStorageNodePayload & { id: string }) =>
      storageApi.updateNode(tenantId as string, id, payload),
    onSuccess: invalidate,
  })
}

export function useMoveStorageNode() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: ({ id, newParentId }: { id: string; newParentId: string | null }) =>
      storageApi.moveNode(tenantId as string, id, newParentId),
    onSuccess: invalidate,
  })
}

/** Blocking an occupied position is refused server-side — surface the 409. */
export function useSetPositionBlocked() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) =>
      storageApi.setBlocked(tenantId as string, id, blocked),
    onSuccess: invalidate,
  })
}

export function useSetLotLocation() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ lotId, storageNodeId }: { lotId: string; storageNodeId: string | null }) =>
      storageApi.setLotLocation(tenantId as string, lotId, storageNodeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] })
      // Occupancy changed, so any material/lot list showing a location is stale.
      void qc.invalidateQueries({ queryKey: ['inventory'] })
    },
  })
}

export function useSetProductLocation() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, storageNodeId }: { productId: string; storageNodeId: string | null }) =>
      storageApi.setProductLocation(tenantId as string, productId, storageNodeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] })
      // The product row itself now carries its location (`storagePath`).
      void qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useDeleteStorageNode() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useStorageInvalidator()
  return useMutation({
    mutationFn: (id: string) => storageApi.deleteNode(tenantId as string, id),
    onSuccess: invalidate,
  })
}
